/**
 * What the Brand Guidelines view works out from the token set the API sends (ORB-904). Pure: no fetching, no rendering.
 *
 * ⚑ The API resolves every alias, theme and role before the set reaches here. Nothing in this file resolves a token;
 * it groups, labels and measures what was resolved, so the view cannot disagree with emails and pages about a color.
 */

import type {
  BrandAssetType,
  BrandColorTokenViewDto,
  BrandRolesViewDto,
  BrandTokenFamilyKind,
  BrandTokenPatchDto,
  BrandTokenRole,
  BrandTokenRolesPatchDto,
  BrandTokenSetViewDto,
  BrandTokenViewDto,
  BrandTypeFamilyPatchDto,
  BrandTypeGroupViewDto,
  BrandTypeStylePatchDto,
  BrandTypeStyleViewDto,
} from '@/types/knowledgeBase'
import { parseServerDate, parseServerTimestamp } from '@/utils/time'

export type ColorRole = 'Primary' | 'Secondary' | 'Accent' | 'Background' | 'Text'
export type TypeRole = 'Heading' | 'Body'
export type ValueRole = 'Radius' | 'Shadow'

/** Every name starting with this is one of the nine roles, never a token of the brand's own. */
export const RESERVED_PREFIX = 'orbitalx-'
/** The type group holding the heading and body roles, which are aliases of the brand's own styles. */
export const RESERVED_TYPE_GROUP = 'OrbitalX'

/** Said on a role when its reserved token carries no usage of its own. */
export const COLOR_ROLES: readonly { role: ColorRole; usage: string }[] = [
  { role: 'Primary', usage: 'Buttons and links in emails and pages.' },
  { role: 'Secondary', usage: 'Secondary panels in emails and pages.' },
  { role: 'Accent', usage: 'The one call to action.' },
  { role: 'Background', usage: 'Email and page background.' },
  { role: 'Text', usage: 'Body text in emails and pages.' },
]
export const TYPE_ROLES: readonly { role: TypeRole; usage: string }[] = [
  { role: 'Heading', usage: 'Headlines in emails and pages.' },
  { role: 'Body', usage: 'Paragraphs in emails and pages.' },
]
export const VALUE_ROLES: readonly { role: ValueRole; usage: string }[] = [
  { role: 'Radius', usage: 'Buttons and cards.' },
  { role: 'Shadow', usage: 'Raised cards and menus.' },
]

export const LOGO_SLOTS: readonly { type: BrandAssetType; label: string }[] = [
  { type: 'Logo', label: 'Logo' },
  { type: 'Icon', label: 'Icon' },
  { type: 'Banner', label: 'Banner' },
  { type: 'OgImage', label: 'Social image' },
  { type: 'Favicon', label: 'Favicon' },
]

export function isReserved(name: string): boolean {
  return name.startsWith(RESERVED_PREFIX)
}

/** What a role says it is for: its reserved token's own usage when the brand wrote one, otherwise OrbitalX's. */
export function roleUsage(set: BrandTokenSetViewDto | null, role: BrandTokenRole, fallback: string): string {
  if (set === null || isTypeRole(role)) return fallback
  const tokenName = set.Roles[role].TokenName
  const tokens: { Name: string; Usage?: string | null }[] = role === 'Radius' ? set.Radius : role === 'Shadow' ? set.Shadow : set.Colors
  return tokens.find(token => token.Name === tokenName)?.Usage?.trim() || fallback
}

/** The token or style a role names directly, which is what its editor starts on. */
export function roleTarget(roles: BrandRolesViewDto | null, role: BrandTokenRole): string {
  return roles?.[role].AliasChain[0] ?? ''
}

/** The role's value as the patch would write it now, so an editor can tell a change from a re-save. */
export function currentRoleValue(roles: BrandRolesViewDto | null, role: BrandTokenRole): string {
  if (roles === null) return ''
  const target = roleTarget(roles, role)
  if (isTypeRole(role)) return target || (roles[role].StyleName ?? '')
  if (target !== '') return `{${target}}`
  return isValueRole(role) ? roles[role].Value ?? '' : roles[role].Hex ?? ''
}

export function isTypeRole(role: BrandTokenRole): role is TypeRole {
  return role === 'Heading' || role === 'Body'
}

export function isValueRole(role: BrandTokenRole): role is ValueRole {
  return role === 'Radius' || role === 'Shadow'
}

/** A role editor's choice as the patch value: a typed literal wins over a chosen token, and a style is sent by name. */
export function chosenRoleValue(role: BrandTokenRole, token: string, literal: string): string {
  if (isTypeRole(role)) return token
  const typed = literal.trim()
  if (typed !== '') return typed
  return token === '' ? '' : `{${token}}`
}

export function rolePatch(role: BrandTokenRole, value: string): BrandTokenRolesPatchDto {
  return { [role]: value }
}

/**
 * One role being changed in place: the token or style chosen, a typed value, the API's refusal of either, and whether
 * the person is being asked to confirm removing the role.
 */
export interface RoleEditor {
  role: BrandTokenRole
  token: string
  literal: string
  error: string | null
  removing: boolean
}

export function openRoleEditor(roles: BrandRolesViewDto | null, role: BrandTokenRole): RoleEditor {
  return { role, token: roleTarget(roles, role), literal: '', error: null, removing: false }
}

/** What removing a role costs, asked before it is sent. Emails refuse creation, sign-off and sending without these two. */
export function removeRoleQuestion(role: BrandTokenRole): string {
  return role === 'Background' || role === 'Text'
    ? `Remove ${role}? Emails cannot be created or sent without it.`
    : `Remove ${role}?`
}

/** Whether saving would change anything: nothing chosen, or the value the role already has, is not a change. */
export function roleEditorChanged(roles: BrandRolesViewDto | null, editor: RoleEditor): boolean {
  const value = chosenRoleValue(editor.role, editor.token, editor.literal)
  const current = currentRoleValue(roles, editor.role)
  if (value === '') return false
  return value.startsWith('#') ? value.toUpperCase() !== current.toUpperCase() : value !== current
}

/** A role's problem in words. A stored set has passed the grammar, so missing is the one a person meets. */
export function problemText(problem: string | null | undefined): string {
  if (!problem || problem === 'missing') return 'Not set'
  return problem.charAt(0).toUpperCase() + problem.slice(1)
}

/** Which roles point at each token or style by name, for the pill beside it in the full lists. */
export function rolesPointingAt(roles: BrandRolesViewDto | null): Map<string, string[]> {
  const pointers = new Map<string, string[]>()
  if (roles === null) return pointers
  const add = (name: string | null | undefined, role: string) => {
    if (!name) return
    pointers.set(name, [...(pointers.get(name) ?? []), role])
  }
  for (const { role } of COLOR_ROLES) add(roles[role].AliasChain[0], role)
  for (const { role } of VALUE_ROLES) add(roles[role].AliasChain[0], role)
  for (const { role } of TYPE_ROLES) add(roles[role].StyleName, role)
  return pointers
}

export interface ColorGroup {
  label: string
  /** The name the group is taken from, which a color added to it starts with. */
  stem: string
  tokens: BrandColorTokenViewDto[]
}

/** The name a color's group is taken from, without its trailing step number: plum-900 is plum. */
export function colorStem(name: string): string {
  return name.replace(/[-_.]\d+$/, '')
}

/** The brand's own colors, grouped by the name before a trailing step number: plum-900 and plum-100 are Plum. */
export function colorGroups(colors: readonly BrandColorTokenViewDto[]): ColorGroup[] {
  const groups = new Map<string, BrandColorTokenViewDto[]>()
  for (const token of colors) {
    if (isReserved(token.Name)) continue
    const stem = colorStem(token.Name)
    groups.set(stem, [...(groups.get(stem) ?? []), token])
  }
  return [...groups].map(([stem, tokens]) => ({ label: labelOf(stem), stem, tokens }))
}

function labelOf(stem: string): string {
  const words = stem.replace(/[-_.]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function ownTokens<T extends { Name: string }>(tokens: readonly T[]): T[] {
  return tokens.filter(token => !isReserved(token.Name))
}

export function ownTypeGroups(groups: readonly BrandTypeGroupViewDto[] | undefined): BrandTypeGroupViewDto[] {
  return (groups ?? []).filter(group => group.Name !== RESERVED_TYPE_GROUP)
}

/** An alias's target when the stored value is one, as `{name}`. */
export function aliasOf(token: Pick<BrandTokenViewDto, 'Value'>): string | null {
  const match = /^\{([^{}]+)\}$/.exec(token.Value?.trim() ?? '')
  return match ? match[1] : null
}

/** WCAG 2 contrast between two opaque `#RRGGBB` colors, or null when either is not one. */
export function contrastRatio(foreground: string | null | undefined, background: string | null | undefined): number | null {
  const a = luminance(foreground)
  const b = luminance(background)
  if (a === null || b === null) return null
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

function luminance(hex: string | null | undefined): number | null {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex ?? '')
  if (!match) return null
  const [red, green, blue] = match.slice(1).map(channel => {
    const value = parseInt(channel, 16) / 255
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export type ContrastGrade = { label: string; tone: 'g' | 'a' | 'r' }

/** The WCAG level for body-size text, in words. */
export function contrastGrade(ratio: number): ContrastGrade {
  if (ratio >= 7) return { label: 'AAA', tone: 'g' }
  if (ratio >= 4.5) return { label: 'AA', tone: 'g' }
  if (ratio >= 3) return { label: 'Large text only', tone: 'a' }
  return { label: 'Too low for text', tone: 'r' }
}

/** One decimal, never rounded up past a threshold it did not reach: 4.49 reads 4.4, not 4.5. */
export function formatRatio(ratio: number): string {
  return `${(Math.floor(ratio * 10) / 10).toFixed(1)}:1`
}

const GENERIC_FAMILIES = new Set([
  'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'math', 'emoji', 'fangsong',
  'system-ui', 'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded', '-apple-system', 'blinkmacsystemfont',
])

/** The first face of a CSS font stack without its quotes, or null when it is a generic. Mirrors the API's resolver. */
export function firstFamilyName(stack: string | null | undefined): string | null {
  const first = (stack ?? '').split(',')[0].trim()
  const quoted = first.length >= 2 && (first[0] === '"' || first[0] === "'") && first.endsWith(first[0])
  if (quoted) return first.slice(1, -1).trim() || null
  return first === '' || GENERIC_FAMILIES.has(first.toLowerCase()) ? null : first
}

/** The stack with its first face taken off: what the browser shows when that face cannot be loaded. */
export function systemFallback(stack: string | null | undefined): string {
  const rest = (stack ?? '').split(',').slice(1).map(part => part.trim()).filter(Boolean)
  return rest.length > 0 ? rest.join(', ') : 'system-ui, sans-serif'
}

/** Weights a style asks for, as numbers: `600` is one and a variable range `300 800` is both ends. */
export function weightsOf(fontWeight: string | null | undefined): number[] {
  return (fontWeight ?? '').trim().split(/\s+/)
    .map(Number)
    .filter(weight => Number.isInteger(weight) && weight >= 1 && weight <= 1000)
}

export interface FontRequest {
  family: string
  weights: number[]
}

/**
 * Every named face the specimens set, with the weights they set it in, so each loads once. A declared family nothing
 * uses is asked for with no weights, since the current app draws every family's tile in it.
 */
export function fontRequests(set: BrandTokenSetViewDto | null): FontRequest[] {
  const byFamily = new Map<string, Set<number>>()
  const want = (stack: string | null | undefined, fontWeight: string | null | undefined) => {
    const family = firstFamilyName(stack)
    if (family === null) return
    const weights = byFamily.get(family) ?? new Set<number>()
    weightsOf(fontWeight).forEach(weight => weights.add(weight))
    byFamily.set(family, weights)
  }
  for (const group of ownTypeGroups(set?.Type?.Groups)) {
    for (const style of group.Styles) want(set?.Type?.Families[group.Family], style.FontWeight)
  }
  for (const { role } of TYPE_ROLES) want(set?.Roles[role].FamilyStack, set?.Roles[role].FontWeight)
  for (const stack of Object.values(set?.Type?.Families ?? {})) want(stack, null)
  return [...byFamily].map(([family, weights]) => ({ family, weights: [...weights].sort((a, b) => a - b) }))
}

/** A Google Fonts css2 address for one face. The weights are ascending, which css2 requires. */
export function googleFontsHref({ family, weights }: FontRequest): string {
  const axis = weights.length > 0 ? `:wght@${weights.join(';')}` : ''
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}${axis}&display=swap`
}

/** `40px` and `48px` read 40/48, as a type spec does; anything not in px keeps its unit. */
export function sizeOverLine(fontSize: string | null | undefined, lineHeight: string | null | undefined): string | null {
  if (!fontSize) return null
  if (!lineHeight) return fontSize
  const bothPx = /px$/.test(fontSize) && /px$/.test(lineHeight)
  return bothPx ? `${fontSize.replace(/px$/, '')}/${lineHeight.replace(/px$/, '')}` : `${fontSize}/${lineHeight}`
}

/** One line naming a style: face, size over line height, weight. */
export function styleCaption(family: string | null, style: Pick<BrandTypeStyleViewDto, 'FontSize' | 'LineHeight' | 'FontWeight'>): string {
  return [family ?? 'System font', sizeOverLine(style.FontSize, style.LineHeight), style.FontWeight]
    .filter(Boolean)
    .join(' · ')
}

/** A length in px, reading rem and em at 16px. Null for a percentage or anything else a bar cannot be drawn from. */
export function pxOf(length: string | null | undefined): number | null {
  const match = /^(-?(?:\d+(?:\.\d+)?|\.\d+))(px|rem|em)?$/.exec((length ?? '').trim())
  if (!match) return null
  const value = Number(match[1])
  return match[2] === 'rem' || match[2] === 'em' ? value * 16 : value
}

/** What a specimen of this style says: a headline at headline sizes, a sentence at reading sizes. */
export function specimenText(style: Pick<BrandTypeStyleViewDto, 'FontSize' | 'TextTransform'>): string {
  const size = pxOf(style.FontSize) ?? 16
  if (size >= 24) return 'Clear words, set with care.'
  if (size <= 13 || style.TextTransform === 'uppercase') return 'Labels and captions'
  return 'Paragraphs set in this style read at the size and spacing the brand chose for them.'
}

/** A style's transform and tracking, when it has either: "Uppercase · +0.06em". */
export function styleExtras(style: Pick<BrandTypeStyleViewDto, 'TextTransform' | 'LetterSpacing'>): string | null {
  const tracking = style.LetterSpacing
  const parts = [
    style.TextTransform && style.TextTransform !== 'none' ? style.TextTransform.charAt(0).toUpperCase() + style.TextTransform.slice(1) : null,
    tracking && tracking !== 'normal' && tracking !== '0' ? (tracking.startsWith('-') ? tracking : `+${tracking}`) : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** The type section's opening line, from the faces the heading and body roles resolve to. */
export function typeLede(set: BrandTokenSetViewDto): string {
  const heading = set.Roles.Heading.FamilyName
  const body = set.Roles.Body.FamilyName
  if (heading && body && heading !== body) return `${heading} sets headlines and ${body} sets everything people read.`
  if (heading && body) return `${heading} sets headlines and everything people read.`
  return 'Each style is shown in its own face, at its own size.'
}

/** A day as the brand's provenance and files say it: 18 Sep. */
export function dayOf(value: string | null | undefined): string | null {
  const date = parseServerDate(value ?? null)
  return date ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null
}

/**
 * A sample drawn in the brand's own colors: its primary as the outline, washed over its page background for the fill.
 * Empty when it has no primary, so the sample keeps the view's neutral one.
 */
export function brandTint(primary: string | null | undefined, ground: string | null | undefined): Record<string, string> {
  if (!primary) return {}
  return {
    boxShadow: `inset 0 0 0 1.5px ${primary}`,
    background: `color-mix(in srgb, ${primary} 14%, ${ground ?? '#FFFFFF'})`,
  }
}

/** A list in words: "a", "a and b", "a, b and c". */
export function inWords(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** A file's kind as people name it: its extension, since a Word file's content type runs to seventy characters. */
export function fileKind(contentType: string, fileName: string): string {
  const extension = /\.([a-z0-9]{1,5})$/i.exec(fileName)?.[1]
  if (extension) return extension.toUpperCase()
  if (contentType === 'image/svg+xml') return 'SVG'
  const subtype = contentType.split('/')[1]?.replace(/^x-/, '')
  return subtype && subtype.length <= 5 ? subtype.toUpperCase() : 'File'
}

export function sizeOf(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// ── Changing the brand's own entries (ORB-973) ──

/** What a person changes below the roles: a token of one of four families, a type style or a font family. */
export type TokenKind = BrandTokenFamilyKind | 'Style' | 'Family'

/** One entry as its row names it: its kind, its name, and the group it is listed in. */
export interface TokenRef {
  kind: TokenKind
  name: string
  /** A color's stem or a style's group. Null for a kind listed without groups. */
  slot: string | null
}

/**
 * One entry being added or changed in place. Its fields are keyed by the patch property each fills, so a refusal the
 * API keys by that property lands on the field that holds it.
 */
export interface TokenEditor {
  kind: TokenKind
  /** The name it has now; null while it is being added. */
  current: string | null
  /** The group the form opens under, which a new style also joins. Null for a new group, or a kind without groups. */
  slot: string | null
  fields: Record<string, string>
  /** The fields as the editor opened, so one nobody has typed in is not a change. */
  was: Record<string, string>
  /** Refusals at the field each names, and under {@link FORM_ERROR} what no field on the form owns. */
  errors: Record<string, string>
}

/** Where an editor keeps a refusal that no field on its form owns. */
export const FORM_ERROR = 'Form'

export function openTokenEditor(kind: TokenKind, current: string | null, slot: string | null, fields: Record<string, string>): TokenEditor {
  return { kind, current, slot, fields, was: fields, errors: {} }
}

/** Whether saving would change anything. The API trims what it is sent, so spaces alone are not a change. */
export function tokenEditorChanged(editor: TokenEditor): boolean {
  return Object.keys(editor.fields).some(key => editor.fields[key].trim() !== (editor.was[key] ?? '').trim())
}

/** The name an editor saves under: a font family's key, and every other entry's name. */
export function editorName(editor: TokenEditor): string {
  return (editor.kind === 'Family' ? editor.fields.Key : editor.fields.Name).trim()
}

const NOUNS: Record<TokenKind, string> = {
  Color: 'color', Spacing: 'spacing step', Radius: 'radius', Shadow: 'shadow', Style: 'style', Family: 'font family',
}

/** A kind as the API's refusals name it. */
export function tokenNoun(kind: TokenKind): string {
  return NOUNS[kind]
}

/** How a row names its entry to a reader. A style and a family say what they are: `body` is also a role's name. */
export function entryLabel(kind: TokenKind, name: string): string {
  return kind === 'Style' ? `the ${name} style` : kind === 'Family' ? `the ${name} family` : name
}

/** What Confirm these colors waits on while an entry's form is open: the change to the h1 style, or the new color. */
export function editorWaitLabel(editor: TokenEditor): string {
  return editor.current !== null ? `the change to ${entryLabel(editor.kind, editor.current)}` : `the new ${tokenNoun(editor.kind)}`
}

/** The `data-opener` of a button that opens an editor or asks about a removal, so focus can go back to it. */
export function openerOf(action: 'add' | 'change' | 'remove', kind: TokenKind, name: string | null): string {
  return `${action}:${kind}:${name ?? ''}`
}

/** The button that opened an editor: its row's Change, or the Add of the group it opened under. */
export function editorOpener(editor: TokenEditor): string {
  return editor.current !== null ? openerOf('change', editor.kind, editor.current) : openerOf('add', editor.kind, editor.slot)
}

export type TokenPatch =
  | { kind: BrandTokenFamilyKind; dto: BrandTokenPatchDto }
  | { kind: 'Style'; dto: BrandTypeStylePatchDto }
  | { kind: 'Family'; dto: BrandTypeFamilyPatchDto }

/**
 * The editor as the patch its kind takes. A change names the entry by `Current`, and a name different from it renames
 * it. A new style joins the group it was added under; a style that starts a group names the group and its family.
 */
export function editorPatch(editor: TokenEditor): TokenPatch {
  const { kind, current, slot, fields } = editor
  if (kind === 'Family') return { kind, dto: { Current: current, Key: fields.Key, Stack: fields.Stack, Remove: false } }
  if (kind === 'Style') {
    const group = current !== null ? {} : slot !== null ? { Group: slot } : { Group: fields.Group, Family: fields.Family }
    return {
      kind,
      dto: { Current: current, Name: fields.Name, ...group, FontSize: fields.FontSize, LineHeight: fields.LineHeight, FontWeight: fields.FontWeight, Remove: false },
    }
  }
  return { kind, dto: { Current: current, Name: fields.Name, Value: fields.Value, Usage: fields.Usage, Remove: false } }
}

export function removalPatch({ kind, name }: TokenRef): TokenPatch {
  return { kind, dto: { Current: name, Remove: true } }
}

/**
 * A refusal as an editor shows it: each message at the field on the form it names, and the rest at the form. One that
 * names no field on the form is shown whole, at the form.
 */
export function editorErrors(refusal: { message: string; fieldErrors: Record<string, string> }, fields: readonly string[]): Record<string, string> {
  const errors: Record<string, string> = {}
  const elsewhere: string[] = []
  for (const [field, message] of Object.entries(refusal.fieldErrors)) {
    const key = fields.find(candidate => candidate.toLowerCase() === field.toLowerCase())
    if (key) errors[key] = message
    else elsewhere.push(message)
  }
  if (Object.keys(errors).length === 0) errors[FORM_ERROR] = refusal.message
  else if (elsewhere.length > 0) errors[FORM_ERROR] = elsewhere.join(' ')
  return errors
}

/** A color's value in one theme as it is stored, which its editor starts on: an alias stays an alias. */
export function storedColorValue(token: BrandColorTokenViewDto, themeId: string): string {
  return token.ValuesByTheme?.[themeId] ?? token.Value ?? token.ResolvedHexByTheme[themeId] ?? ''
}

/** A spacing step's, radius's or shadow's value as it is stored in the theme shown, which its editor starts on. */
export function storedTokenValue(token: BrandTokenViewDto, themeId: string | null): string {
  return (themeId !== null ? token.ValuesByTheme?.[themeId] : undefined) ?? token.Value ?? token.ResolvedValue ?? ''
}

/**
 * Whether the set changed after it was confirmed. An edit keeps a confirmed set confirmed and moves its `At` on, and a
 * confirmation moves `ConfirmedAt` past it. A set a person's first edit creates is confirmed at the instant it is made.
 */
export function changedSinceConfirmed(provenance: BrandTokenSetViewDto['Provenance']): boolean {
  if (!provenance.Confirmed) return false
  const at = parseServerTimestamp(provenance.At)
  const confirmedAt = parseServerTimestamp(provenance.ConfirmedAt)
  return at !== null && confirmedAt !== null && at > confirmedAt
}

/** "Changed by Marjo · 29 Sept" while the set has changed since it was confirmed. A change nobody is named for is Webster's. */
export function changedByLine(provenance: BrandTokenSetViewDto['Provenance']): { text: string; byWebster: boolean } | null {
  if (!changedSinceConfirmed(provenance)) return null
  const by = provenance.ByName?.trim() || null
  return { text: [`Changed by ${by ?? 'Webster'}`, dayOf(provenance.At)].filter(Boolean).join(' · '), byWebster: by === null }
}
